'use client';
import React, { useState, useRef, useEffect } from 'react';
import sparrowLogo from '../../assets/images/sparrow.png';
import styles from './SparrowChat.module.css';
import { marked } from 'marked';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { v4 as uuidv4 } from 'uuid';
import usePostSparrowChat from '../../hooks/useSparrowChat'; 

const SparrowChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [contextMemory, setContextMemory] = useState('');
  const [chatId, setChatId] = useState(() => uuidv4());

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const { postChat, cancel } = usePostSparrowChat();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      const welcome = {
        sender: 'assistant',
        text: "👋 Welcome to Sparrow! How can I assist you today?",
      };
      setMessages([welcome]);
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim() && !selectedFile) return;

    const userMessage = { sender: 'user', text: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const { response, contextMemory: updatedMemory } = await postChat({
        message: userMessage.text,
        chatId,
        contextMemory,
        file: selectedFile,
      });

      const botMessage = {
        sender: 'assistant',
        text: response || 'No response.',
      };

      if (updatedMemory) {
        setContextMemory(updatedMemory);
      }

      setMessages([...newMessages, botMessage]);
    } catch (err) {
        console.error(err);
        // handle cancel/time‑out vs. other errors
        const text = err.message === 'Request cancelled or timed out'
            ? 'Request was cancelled.'
            : 'Error talking to Sparrow AI.';
        setMessages((prev) => [...prev, { sender: 'assistant', text }]);
    } finally {
      setLoading(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log('Selected file:', file);
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Context Memory Display */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 4,
            backgroundColor: '#fffde7',
            height: '250px',
            minHeight: '250px',
            overflowY: 'auto'
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Conversation Memory
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {contextMemory || 'No memory yet.'}
          </Typography>
        </Paper>

        {/* Messages */}
        <Paper
          elevation={3}
          sx={{
            overflowY: 'auto',
            p: 2,
            borderRadius: 2,
            bgcolor: '#f5f5f5',
            height: 'calc(100vh - 500px)',
            minHeight: '300px'
          }}
        >
          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}
            >
              <Box
                sx={{
                  maxWidth: '75%',
                  bgcolor: msg.sender === 'user' ? '#1976d2' : '#e0e0e0',
                  color: msg.sender === 'user' ? 'white' : 'black',
                  p: 1.5,
                  borderRadius: 2
                }}
              >
                {msg.sender === 'assistant' ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      component="img"
                      src={sparrowLogo}
                      alt="Sparrow"
                      sx={{ width: 24, height: 24, mt: '4px' }}
                    />
                    <Box
                      component="div"
                      sx={{
                        fontSize: '1rem',
                        textAlign: 'left',
                        lineHeight: 1.6,
                        '& pre': {
                          backgroundColor: '#1f1f1f',
                          color: '#f8f8f2',
                          padding: '8px',
                          borderRadius: '4px',
                          overflowX: 'auto'
                        },
                        '& code': {
                          fontFamily: 'monospace',
                          backgroundColor: '#1d1d1d',
                          color: '#79d6f7',
                          padding: '2px 4px',
                          borderRadius: '4px'
                        },
                        '& a': {
                          color: 'white',
                          textDecoration: 'underline'
                        }
                      }}
                      dangerouslySetInnerHTML={{ __html: marked.parse(msg.text) }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body1">{msg.text}</Typography>
                )}
              </Box>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          <div ref={bottomRef} />
        </Paper>

        {/* Input */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}

            slotProps={{
              input: {
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleFileClick}>
                      <AttachFileIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleSend}
                      disabled={loading || (!input.trim() && !selectedFile)}
                    >
                      <SendIcon />
                    </IconButton>
                    {loading && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          cancel();
                          setLoading(false);
                        }}
                        sx={{ ml: 1 }}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                )
              }
            }}
          />

          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1, ml: 1 }}>
              📎 Attached: {selectedFile.name}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

};

export default SparrowChat;
